import {
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  ShopListItem,
  SpuInfo,
  ShopType,
  PRODUCTS_MAX_NUM,
  PRODUCTS_PSA_MAX_NUM,
  PRODUCTS_SINGLE_NUM,
  ProductAvailability,
  SpuScope,
  SpuOrganicAuditStatus,
  SpuStatus,
  SpuOrganicStatus,
  SpuAvailableFilter,
  SpuInStorefrontFilter,
  SpuAvailableFilterType,
  SpuInStorefrontFilterType,
  IdentityInfo,
  SpuMutexStatus,
  SpuMutexReasonCode,
} from '@tt4b/ttam-enums';
import { Ref, ComputedRef, WritableComputedRef, ref, computed, watch, onMounted } from '@vue/composition-api';
import {
  useShopStore,
  useRequest,
  useGetSpuProductApi,
  useStdCreativeAdgroupIs,
  usePreviewProduct,
} from '@tt4b/ttam-hooks';
import { getSearchFields } from '@tt4b/ttam-utils';
import { i18n } from '../../../i18n';
import { Message } from 'byted-vui';

const PAGE_SIZE = 20;

export const availableParams = {
  availability_status: ProductAvailability.InOrder,
  spu_scope: SpuScope.Approve,
  organic_audit_status: SpuOrganicAuditStatus.Approve,
  organic_status: SpuOrganicStatus.Online,
  status: SpuStatus.Online,
};

export type spuListAmountType = {
  allAmount: number;
  availableAmount: number;
};

export function useShopInfo(opt: { adgroupData: Ref<AdgroupFormData> }): ComputedRef<ShopListItem> {
  const { selectedShop } = useShopStore({
    adgroupFormData: opt.adgroupData,
  });
  const shopInfo = computed(() => selectedShop.value || ({} as ShopListItem));
  return shopInfo;
}
const isSpuMutexDisabled = (spuInfo: SpuInfo): boolean =>
  spuInfo.mutex_status === SpuMutexStatus.Mutex &&
  [SpuMutexReasonCode.ROI2_Occupied, SpuMutexReasonCode.ROI1_Occupied].includes(spuInfo.reason_code);
export const isSpuDisabled = (spuInfo: SpuInfo): boolean => {
  const {
    availability_status, // 有库存
    audit_status, // BI 过审
    organic_audit_status, // GNE审核结果，为空则不过滤
    organic_status, // GNE在线
    status, // BI在线
    ad_service_status, // 是否可用于进行广告投放状态(新接口下才有这个字段)
    mutex_status,
  } = spuInfo || {};

  // 如果有这个字段，直接根据它综合判断即可
  if (typeof ad_service_status !== 'undefined') {
    return mutex_status === SpuMutexStatus.Mutex || ad_service_status !== 1;
  }
  return !(
    availability_status === ProductAvailability.InOrder &&
    audit_status === SpuScope.Approve &&
    organic_audit_status === SpuOrganicAuditStatus.Approve &&
    organic_status === SpuOrganicStatus.Online &&
    status === SpuStatus.Online
  );
};

export function useSpuFilter(currentPage: Ref<number>) {
  // 默认值改为 AVAILABLE
  const spuAvailableFilter = ref(SpuAvailableFilter.AVAILABLE);
  const spuAvailableFilterChange = val => {
    if ([SpuAvailableFilter.ALL, SpuAvailableFilter.AVAILABLE].includes(val)) {
      currentPage.value = 1;
      spuAvailableFilter.value = val;
    }
  };
  const spuAvailableParams: ComputedRef<SpuAvailableFilterType> = computed(() =>
    spuAvailableFilter.value === SpuAvailableFilter.AVAILABLE ? availableParams : {},
  );

  const inStorefrontFilter = ref(SpuInStorefrontFilter.ALL);
  const inStorefrontFilterChange = val => {
    if ([SpuInStorefrontFilter.ALL, SpuInStorefrontFilter.IN, SpuInStorefrontFilter.NOT_IN].includes(val)) {
      currentPage.value = 1;
      inStorefrontFilter.value = val;
    }
  };
  const spuInStorefrontParams: ComputedRef<SpuInStorefrontFilterType> = computed(() =>
    [SpuInStorefrontFilter.IN, SpuInStorefrontFilter.NOT_IN].includes(inStorefrontFilter.value)
      ? {
          show_window_scope: inStorefrontFilter.value,
        }
      : {},
  );

  return {
    spuAvailableFilter,
    spuAvailableFilterChange,
    spuAvailableParams,
    inStorefrontFilter,
    inStorefrontFilterChange,
    spuInStorefrontParams,
  };
}

export function useSpuDisabled(spuInfo: Ref<SpuInfo>) {
  const _isSpuDisabled = computed(() => isSpuDisabled(spuInfo.value));

  return {
    isSpuDisabled: _isSpuDisabled,
  };
}

export function useGetShopProductsDetails(opt: {
  spuIdList?: ComputedRef<Array<string>> | Ref<Array<string>>;
  shopInfo: ComputedRef<ShopListItem> | Ref<ShopListItem>;
  identity?: Ref<IdentityInfo>;
  location?: Ref<string[]>;
  limit?: number;
  page?: Ref<number>;
  useNewSearchApi?: Ref<boolean>;
  isNeedMutexInfo?: Ref<boolean>;
  sortParam?: Ref<{
    sort_order: number;
    sort_field: string;
  }>;
  search?: Ref<{
    field: string;
    value: string;
  }>;
  spuAvailableParams: ComputedRef<SpuAvailableFilterType>;
  spuInStorefrontParams: ComputedRef<SpuInStorefrontFilterType>;
}) {
  const {
    spuIdList,
    identity,
    location,
    shopInfo,
    limit = 20,
    page = ref(1),
    search,
    spuAvailableParams,
    spuInStorefrontParams,
    sortParam,
    useNewSearchApi,
    isNeedMutexInfo,
  } = opt;

  const param = computed(() => ({
    shopId: shopInfo.value.id,
    identityId: identity?.value?.identityId,
    identityType: identity?.value?.identityType,
    location: location?.value,
    catalogId: shopInfo.value.platform_id,
    bcId: shopInfo.value.bc_id,
    isNeedMutexInfo: isNeedMutexInfo || ref(false),
    page_info: useNewSearchApi?.value
      ? {
          page_index: page.value,
          page_size: limit,
        }
      : {
          offset: (page.value - 1) * limit,
          pageSize: limit,
        },
    useNewSearchApi: useNewSearchApi || ref(false),
    search: search?.value,
    sort_param: sortParam?.value,
    ...(spuIdList?.value
      ? {
          search: {
            field: 'spuID',
            value: spuIdList.value,
          },
        }
      : {}),
    ...spuAvailableParams.value,
    ...spuInStorefrontParams.value,
  }));
  const { data, isLoading, run, reset } = useRequest(useGetSpuProductApi, param, {
    manual: true,
  });

  watch(
    param,
    val => {
      if ((val.shopId && val.catalogId && val.bcId) || val.identityId) {
        run();
      }
    },
    { immediate: true },
  );

  return {
    data,
    isLoading,
    run,
    reset,
  };
}

// 是否1P
export function useIsEcomShop(shopInfo: Ref<ShopListItem>) {
  const isEcomShop = computed(() => shopInfo.value.type === ShopType.ecommShop); // 是否1P
  return isEcomShop;
}

// 同步creative中的spuDetailList和抽屉中的selectedSpuList
export function useSelectedSpu(opt: { spuDetailList: Ref<Array<SpuInfo>> }): {
  selectedSpuList: Ref<Array<SpuInfo>>;
  resetSelected: () => void;
} {
  const { spuDetailList } = opt;
  const selectedSpuList = ref([...(spuDetailList.value || [])]);
  watch(spuDetailList, val => {
    selectedSpuList.value = [...(val || [])];
  });
  const resetSelected = () => {
    selectedSpuList.value = [...(spuDetailList.value || [])];
  };

  return {
    selectedSpuList,
    resetSelected,
  };
}

export function useMaxProductsNum(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isSingleSelect?: Ref<boolean>;
}) {
  const { adgroupData, campaignData, isSingleSelect = ref(false) } = opt;
  const { isPSA } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const maxProductsNum = computed(() => {
    if (isSingleSelect.value) {
      return PRODUCTS_SINGLE_NUM;
    }
    if (isPSA.value) {
      return PRODUCTS_PSA_MAX_NUM;
    }
    return PRODUCTS_MAX_NUM;
  });
  return {
    maxProductsNum,
  };
}

export function useSelectRow(opt: {
  selectedSpuList: Ref<Array<SpuInfo>>;
  currentPageSpuList: Ref<Array<SpuInfo>>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isSingleSelect?: Ref<boolean>;
}) {
  const { selectedSpuList, currentPageSpuList, adgroupData, campaignData, isSingleSelect = ref(false) } = opt;
  const { maxProductsNum: maxLimit } = useMaxProductsNum({
    adgroupData,
    campaignData,
    isSingleSelect,
  });

  const selectedSpuSet = computed(() => new Set(selectedSpuList.value.map(item => item?.spu_id)));
  const select = row => {
    if (!row?.spu_id || selectedSpuSet.value.has(row.spu_id) || row.isDisabled) {
      return;
    }
    if (isSingleSelect.value) {
      selectedSpuList.value.pop();
      selectedSpuSet.value.clear();
    } else if (maxLimit.value - selectedSpuList.value?.length <= 0) {
      Message({
        message: i18n.t('module_cla_creative_choose_product_limit_reached', { 0: maxLimit.value }) as string,
        type: 'warning',
      });
      return;
    }
    selectedSpuList.value.push(row);
    selectedSpuSet.value.add(row.spu_id);
  };
  const unSelect = row => {
    if (!row?.spu_id || !selectedSpuSet.value.has(row.spu_id)) {
      return;
    }
    const selectedList = selectedSpuList.value;
    const index = selectedList.map(item => item.spu_id).indexOf(row.spu_id);
    if (index >= 0) {
      selectedList.splice(index, 1);
      selectedSpuSet.value.delete(row.spu_id);
    }
  };
  const clearSelection = () => {
    currentPageSpuList.value.forEach(unSelect);
  };
  const selectChange = row => {
    if (!row?.spu_id) {
      return;
    }
    const isSelected = selectedSpuSet.value.has(row.spu_id);
    if (!isSelected) {
      select(row);
    } else {
      unSelect(row);
    }
  };
  const selectAllChange = () => {
    // 根据是否存在可选但未选的商品，决定执行全选还是全反选
    const leftLength = maxLimit.value - selectedSpuList.value?.length;
    const unSelectedRows = currentPageSpuList.value.filter(
      row => !selectedSpuSet.value.has(row.spu_id) && !row.isDisabled,
    );
    const selectableRows = unSelectedRows.slice(0, Math.min(leftLength, unSelectedRows.length));
    const unSelectableRows =
      selectableRows.length < unSelectedRows.length ? unSelectedRows.slice(selectableRows.length) : [];
    const hasSelected = currentPageSpuList.value?.some?.(item => selectedSpuSet.value.has(item.spu_id));
    if (selectableRows.length) {
      if (unSelectableRows.length) {
        Message({
          message: i18n.t('module_cla_creative_choose_product_limit_reached', { 0: maxLimit.value }) as string,
          type: 'warning',
        });
      }
      // 全选当前页
      selectableRows.forEach(select);
    } else if (!hasSelected && unSelectedRows.length) {
      // 当前页没有已选，且有未选的可用商品但没有可选，即全选操作但已达上线，需要tips提示
      Message({
        message: i18n.t('module_cla_creative_choose_product_limit_reached', { 0: maxLimit.value }) as string,
        type: 'warning',
      });
    } else {
      clearSelection();
    }
  };
  return {
    selectedSpuSet,
    selectChange,
    selectAllChange,
    selectNumLimit: maxLimit,
  };
}

// 展开
export function useExpandRow() {
  const expandedRows = ref([]);
  const expandRow = (_row, _expandedRows) => {
    expandedRows.value = _expandedRows;
  };
  const expandRowKeys = computed(() => expandedRows.value.map(({ spu }) => spu.spu_id));
  return {
    expandedRows,
    expandRowKeys,
    expandRow,
  };
}

// 分页信息
export function usePagianation() {
  const currentPage = ref(1);
  const handlePageChange = val => {
    currentPage.value = val;
  };
  return {
    handlePageChange,
    currentPage,
  };
}

// 条件查询
export function useSearchCondition(isEcomShop: ComputedRef<boolean>) {
  const search = ref({
    field: '',
    value: '',
  });
  const fieldList = computed(() => getSearchFields(isEcomShop.value));
  return {
    search,
    fieldList,
  };
}

// 获取商品详情列表
export function useProductsList(opt: {
  shopInfo: Ref<ShopListItem>;
  currentPage: Ref<number>;
  useNewSearchApi?: Ref<boolean>;
  isNeedMutexInfo?: Ref<boolean>;
  sortParam?: Ref<{
    sort_order: number;
    sort_field: string;
  }>;
  search?: Ref<{
    field: string;
    value: string;
  }>;
  spuAvailableParams: ComputedRef<SpuAvailableFilterType>;
  spuInStorefrontParams: ComputedRef<SpuInStorefrontFilterType>;
  identity?: Ref<IdentityInfo>; // showcase用
  location?: Ref<string[]>; // showcase用
}) {
  const {
    shopInfo,
    currentPage,
    search,
    spuAvailableParams,
    spuInStorefrontParams,
    useNewSearchApi,
    isNeedMutexInfo,
    sortParam,
    identity,
    location,
  } = opt;
  const {
    data,
    isLoading,
    run: doRequest,
    reset,
  } = useGetShopProductsDetails({
    shopInfo,
    limit: useNewSearchApi.value ? PRODUCTS_PSA_MAX_NUM : PRODUCTS_MAX_NUM,
    page: currentPage,
    search,
    spuAvailableParams,
    spuInStorefrontParams,
    sortParam,
    useNewSearchApi,
    isNeedMutexInfo,
    identity,
    location,
  });
  const spuInfos = computed(() => data?.value?.spu_infos || []);
  const statCount = computed(() => data?.value?.stat_count);
  const totalCount = computed(() => data?.value?.total_count || 0);

  const spuList = computed(() =>
    spuInfos.value.map(
      item =>
        ({
          ...item.spu,
          skuInfos: item.sku_infos || [],
          isDisabled: isSpuDisabled(item.spu),
          isMutexDisabled: isSpuMutexDisabled(item.spu),
        } as SpuInfo),
    ),
  );

  return {
    spuList,
    isLoading,
    doRequest,
    reset,
    statCount,
    totalCount,
  };
}

// 获取All，Available两个filter的商品数量
export function useGetAmount(opt: {
  identity?: Ref<IdentityInfo>;
  location?: Ref<string[]>;
  shopInfo?: Ref<ShopListItem>;
  spuInStorefrontParams: ComputedRef<SpuInStorefrontFilterType>;
  useNewSearchApi?: Ref<boolean>;
  isNeedMutexInfo?: Ref<boolean>;
}): { spuListAmount: Ref<spuListAmountType>; fetchAmount: () => void } {
  const { identity, location, shopInfo, spuInStorefrontParams, useNewSearchApi, isNeedMutexInfo } = opt;
  const allDataParam = computed(() => ({
    shopId: shopInfo?.value.id,
    identityId: identity?.value?.identityId,
    identityType: identity?.value?.identityType,
    location: location?.value,
    catalogId: shopInfo?.value.platform_id,
    bcId: shopInfo?.value.bc_id,
    useNewSearchApi,
    isNeedMutexInfo,
    ...spuInStorefrontParams.value,
  }));
  const availableDataParam = computed(() => ({
    ...allDataParam.value,
    ...availableParams,
  }));
  // 记录下all products下的所有商品信息
  const { selectProducts } = usePreviewProduct({});
  const { run } = useRequest(useGetSpuProductApi, null, {
    manual: true,
  });
  const spuListAmount = ref<spuListAmountType>({
    allAmount: 0,
    availableAmount: 0,
  });

  const fetchAmount = (): void => {
    // 有值才开始去取
    if (shopInfo?.value.id || (location?.value && identity?.value?.identityId)) {
      run({
        ...allDataParam.value,
      }).then(({ total_count }) => {
        spuListAmount.value.allAmount = total_count ?? 0;
      });
      run({
        ...availableDataParam.value,
      }).then(({ total_count, spu_infos }) => {
        const spuList = (spu_infos || []).map(
          item =>
            ({
              ...item.spu,
              skuInfos: item.sku_infos || [],
              isDisabled: isSpuDisabled(item.spu),
              isMutexDisabled: isSpuMutexDisabled(item.spu),
            } as SpuInfo),
        );
        selectProducts(spuList);

        spuListAmount.value.availableAmount = total_count ?? 0;
      });
    }
  };

  onMounted(() => {
    // showcase场景
    if (identity?.value && location?.value) {
      watch(
        [identity, location],
        () => {
          fetchAmount();
        },
        { immediate: true },
      );
    } else {
      // shop场景
      watch(
        shopInfo,
        _shopInfo => {
          if (_shopInfo?.id) {
            fetchAmount();
          }
        },
        {
          immediate: true,
        },
      );
    }
  });

  return { spuListAmount, fetchAmount };
}

// 总页码计算
export function usePageCount(totalCount: ComputedRef<number>) {
  const pageCount = computed(() => Math.ceil((totalCount.value || 0) / PAGE_SIZE));
  return {
    pageCount,
  };
}

// 抽屉控制
export function useVisible(opt: { visible: Ref<boolean>; isRtl: Ref<boolean>; emit; resetSelected }) {
  const { visible, isRtl, emit, resetSelected } = opt;
  const slipVisible = computed({
    get: () => visible.value,
    set: val => {
      emit('update:visible', val);
    },
  });
  const slideTransition = computed(() => (isRtl.value ? 'slide-left' : 'slide-right'));
  watch(slipVisible, resetSelected);
  return {
    slipVisible,
    slideTransition,
  };
}

export function useAction(opt: {
  selectedSpuList: Ref<Array<SpuInfo>>;
  emit;
  slipVisible: WritableComputedRef<boolean>;
}) {
  const { emit, selectedSpuList, slipVisible } = opt;
  const back = () => {
    slipVisible.value = false;
  };
  const confirm = () => {
    emit('confirm', [...selectedSpuList.value]);
    back();
  };
  return {
    back,
    confirm,
  };
}

export function getProductAmount(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  identity?: Ref<IdentityInfo>;
  location?: Ref<string[]>;
  shopInfo?: Ref<ShopListItem>;
}) {
  const { adgroupData, campaignData, identity, location, shopInfo } = opt;
  const { currentPage } = usePagianation();
  const { is1pShop, isProductFromShowcase } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const useNewSearchApi = computed(() => isProductFromShowcase.value || is1pShop.value);
  const isNeedMutexInfo = computed(() => is1pShop.value);

  const { spuInStorefrontParams } = useSpuFilter(currentPage);
  const { spuListAmount, fetchAmount } = useGetAmount({
    shopInfo,
    spuInStorefrontParams,
    useNewSearchApi,
    isNeedMutexInfo,
    identity,
    location,
  });

  return { spuListAmount, fetchAmount };
}
